import React from "react";
import { SectionHeader } from "@/components/Common/SectionHeader/SectionHeader";
import { cn } from "@/lib/utils";

export const CREATE_FORM_STICKY_FILTERS_SLOT_ID = "create-form-sticky-filters-slot";

export type CreateFormStepNavItem = {
  id: string;
  label: string;
};

type CreateFormStepsNavProps = {
  steps: CreateFormStepNavItem[];
  /** 0-based index of the currently active step */
  currentStepIndex: number;
  title?: string;
  description?: React.ReactNode;
  className?: string;
  onStepClick?: (index: number) => void;
  /** Optional actions rendered opposite the step title (e.g. saved configs). */
  actions?: React.ReactNode;
  /** Marks the step title when this step’s content is included in saved configs. */
  savedInConfig?: boolean;
};

export const CreateFormStepsNav: React.FC<CreateFormStepsNavProps> = ({
  steps,
  currentStepIndex,
  title,
  description,
  className,
  onStepClick,
  actions,
  savedInConfig = false
}) => {
  return (
    <div
      id="create-form-steps-nav"
      data-sticky-stack
      className={cn(
        "sticky top-0 z-10 border-b border-border bg-background pt-6",
        className
      )}
    >
      {(title || description || actions) && (
        <div className="mb-4 flex flex-col gap-3 px-6 sm:flex-row sm:items-start sm:justify-between lg:px-8">
          {(title || description) && (
            <SectionHeader
              title={title}
              subtitle={description}
              className="min-w-0 flex-1"
              savedInConfig={savedInConfig}
            />
          )}
          {actions ? <div className="shrink-0 sm:pt-0.5">{actions}</div> : null}
        </div>
      )}

      <div className="mb-4 flex flex-row gap-4 px-6 lg:px-8">
        {steps.map((step, index) => {
          const isCompleted = currentStepIndex > index;
          const isCurrent = currentStepIndex === index;
          const isClickable = isCompleted && !!onStepClick;

          return (
            <div
              key={step.id}
              className={cn(
                "flex flex-1 items-center gap-4 border-b-4 pb-4",
                isCurrent
                  ? "border-blue-light-300"
                  : isCompleted
                    ? "border-blue-light-400"
                    : "border-border",
                isClickable && "cursor-pointer"
              )}
              onClick={() => isClickable && onStepClick(index)}
            >
              <div
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium",
                  isCurrent
                    ? "bg-blue-light-300 text-white"
                    : isCompleted
                      ? "bg-blue-light-400 text-white"
                      : "border border-border bg-background text-foreground"
                )}
              >
                {index + 1}
              </div>
              <span className="whitespace-nowrap text-sm font-medium text-foreground">
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* GPU/hardware filters portal target — keeps filters fixed with this sticky header */}
      <div id={CREATE_FORM_STICKY_FILTERS_SLOT_ID} className="empty:hidden" />
    </div>
  );
};
